import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

type ThemeMode = 'dark' | 'light';
type Direction = 'inbound' | 'outbound';
type FolderCategory = 'active' | 'archive';

interface TechnogymSftpFolder {
  id: string;
  label: string;
  path: string;
  direction: Direction;
  category: FolderCategory;
  description: string;
}

interface TechnogymSftpFile {
  filename: string;
  size: number | null;
  modified_at: string | null;
  extension: string;
  folder_id: string;
  direction: Direction;
  category: FolderCategory;
}

interface TechnogymSftpListResponse {
  success: boolean;
  message: string;
  generated_at: string;
  folder: TechnogymSftpFolder;
  folders: TechnogymSftpFolder[];
  files: TechnogymSftpFile[];
}

interface TechnogymSftpContentFile {
  filename: string;
  size: number | null;
  modified_at: string | null;
  extension: string;
  content_type: string;
  content_base64: string;
  content_bytes: number;
  truncated: boolean;
  sha256?: string | null;
}

interface TechnogymSftpContentResponse {
  success: boolean;
  message: string;
  generated_at: string;
  folder: TechnogymSftpFolder;
  file: TechnogymSftpContentFile;
}

@Component({
  selector: 'app-technogym-sftp-browser',
  templateUrl: './technogym-sftp-browser.component.html',
  styleUrls: ['./technogym-sftp-browser.component.scss']
})
export class TechnogymSftpBrowserComponent implements OnInit {
  folders: TechnogymSftpFolder[] = [];
  files: TechnogymSftpFile[] = [];
  selectedFolder: TechnogymSftpFolder | null = null;
  selectedFolderId = 'delivery_requests';
  selectedFile: TechnogymSftpFile | null = null;
  previewFile: TechnogymSftpContentFile | null = null;
  previewText = '';
  csvHeaders: string[] = [];
  csvRows: string[][] = [];
  searchText = '';
  generatedAt = '';
  errorMessage = '';
  loading = false;
  previewingFilename = '';
  downloadingFilename = '';
  themeMode: ThemeMode = 'dark';
  fileDisplayLimit = 250;

  readonly previewRowLimit = 200;
  readonly fileDisplayStep = 250;

  private readonly apiUrl = '/api/technogym_sftp/technogym_sftp.php';
  private readonly themeStorageKey = 'technogym-sftp-browser-theme';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initializeTheme();
    this.loadFolder(this.selectedFolderId);
  }

  get activeFolders(): TechnogymSftpFolder[] {
    return this.folders.filter((folder) => folder.category === 'active');
  }

  get archiveFolders(): TechnogymSftpFolder[] {
    return this.folders.filter((folder) => folder.category === 'archive');
  }

  get filteredFiles(): TechnogymSftpFile[] {
    const query = this.searchText.trim().toLowerCase();
    if (!query) {
      return this.files;
    }

    return this.files.filter((file) => file.filename.toLowerCase().includes(query));
  }

  get visibleFiles(): TechnogymSftpFile[] {
    return this.filteredFiles.slice(0, this.fileDisplayLimit);
  }

  get hasMoreFiles(): boolean {
    return this.visibleFiles.length < this.filteredFiles.length;
  }

  get totalBytes(): number {
    return this.files.reduce((total, file) => total + (file.size ?? 0), 0);
  }

  get isCsvPreview(): boolean {
    return this.previewFile?.extension === 'csv' && this.csvHeaders.length > 0;
  }

  get previewStatus(): string {
    if (this.previewingFilename) {
      return 'Loading preview';
    }

    if (!this.previewFile) {
      return 'Select a file';
    }

    return this.previewFile.truncated ? 'Preview truncated at 512 KB' : 'Full file preview';
  }

  loadFolder(folderId: string): void {
    if (this.loading) {
      return;
    }

    this.selectedFolderId = folderId;
    this.loading = true;
    this.errorMessage = '';
    this.searchText = '';
    this.fileDisplayLimit = this.fileDisplayStep;
    this.clearPreview();

    const params = new HttpParams()
      .set('action', 'list')
      .set('folder', folderId);

    this.http.get<TechnogymSftpListResponse>(this.apiUrl, { params }).subscribe({
      next: (response) => {
        this.folders = response.folders ?? [];
        this.files = response.files ?? [];
        this.selectedFolder = response.folder ?? this.folders.find((folder) => folder.id === folderId) ?? null;
        this.generatedAt = response.generated_at ?? '';
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.files = [];
        this.selectedFolder = this.folders.find((folder) => folder.id === folderId) ?? null;
        this.showError(error, 'Could not load the Technogym SFTP folder');
      }
    });
  }

  refresh(): void {
    this.loadFolder(this.selectedFolderId);
  }

  resetFileDisplayLimit(): void {
    this.fileDisplayLimit = this.fileDisplayStep;
  }

  showMoreFiles(): void {
    this.fileDisplayLimit += this.fileDisplayStep;
  }

  preview(file: TechnogymSftpFile): void {
    if (this.previewingFilename || this.downloadingFilename) {
      return;
    }

    this.selectedFile = file;
    this.previewingFilename = file.filename;
    this.errorMessage = '';

    const params = new HttpParams()
      .set('action', 'preview')
      .set('folder', this.selectedFolderId)
      .set('filename', file.filename);

    this.http.get<TechnogymSftpContentResponse>(this.apiUrl, { params }).subscribe({
      next: (response) => {
        this.previewFile = response.file;
        this.previewText = this.decodeBase64(response.file.content_base64);
        this.buildCsvPreview(response.file);
        this.previewingFilename = '';
      },
      error: (error: HttpErrorResponse) => {
        this.previewingFilename = '';
        this.showError(error, `Could not preview ${file.filename}`);
      }
    });
  }

  download(file: TechnogymSftpFile, event?: Event): void {
    event?.stopPropagation();

    if (this.previewingFilename || this.downloadingFilename) {
      return;
    }

    this.downloadingFilename = file.filename;
    this.errorMessage = '';

    const params = new HttpParams()
      .set('action', 'download')
      .set('folder', this.selectedFolderId)
      .set('filename', file.filename);

    this.http.get<TechnogymSftpContentResponse>(this.apiUrl, { params }).subscribe({
      next: (response) => {
        const bytes = this.base64Bytes(response.file.content_base64);
        const blob = new Blob([bytes], { type: response.file.content_type || 'text/plain' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = response.file.filename;
        anchor.click();
        URL.revokeObjectURL(url);
        this.downloadingFilename = '';
        this.toastr.success(`${response.file.filename} downloaded`);
      },
      error: (error: HttpErrorResponse) => {
        this.downloadingFilename = '';
        this.showError(error, `Could not download ${file.filename}`);
      }
    });
  }

  clearPreview(): void {
    this.selectedFile = null;
    this.previewFile = null;
    this.previewText = '';
    this.csvHeaders = [];
    this.csvRows = [];
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  directionLabel(direction: Direction): string {
    return direction === 'inbound' ? 'From SAP' : 'To SAP';
  }

  directionIcon(direction: Direction): string {
    return direction === 'inbound' ? 'fa-solid fa-arrow-down' : 'fa-solid fa-arrow-up';
  }

  fileIcon(file: TechnogymSftpFile): string {
    return file.extension === 'csv' ? 'fa-solid fa-file-csv' : 'fa-regular fa-file-lines';
  }

  formatBytes(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
      return 'Unknown size';
    }

    if (bytes === 0) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, index);
    return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return 'Unknown date';
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleString([], {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
  }

  trackByFolder(_: number, folder: TechnogymSftpFolder): string {
    return folder.id;
  }

  trackByFile(_: number, file: TechnogymSftpFile): string {
    return `${file.folder_id}:${file.filename}`;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private initializeTheme(): void {
    const stored = localStorage.getItem(this.themeStorageKey);
    if (stored === 'dark' || stored === 'light') {
      this.themeMode = stored;
      return;
    }

    this.themeMode = window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }

  private showError(error: HttpErrorResponse, fallback: string): void {
    const message = String(error.error?.message ?? error.message ?? fallback);
    this.errorMessage = message;
    this.toastr.error(message, 'Technogym SFTP');
  }

  private decodeBase64(value: string): string {
    return new TextDecoder('utf-8', { fatal: false }).decode(this.base64Bytes(value));
  }

  private base64Bytes(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  }

  private buildCsvPreview(file: TechnogymSftpContentFile): void {
    this.csvHeaders = [];
    this.csvRows = [];

    if (file.extension !== 'csv') {
      return;
    }

    const rows = this.parseCsv(this.previewText, this.previewRowLimit + 1);
    if (!rows.length) {
      return;
    }

    const columnLimit = 50;
    this.csvHeaders = rows[0].slice(0, columnLimit).map((header, index) => header.trim() || `Column ${index + 1}`);
    this.csvRows = rows.slice(1, this.previewRowLimit + 1).map((row) => {
      const normalized = this.csvHeaders.map((_, index) => row[index] ?? '');
      return normalized.slice(0, columnLimit);
    });
  }

  private parseCsv(content: string, rowLimit: number): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;

    for (let index = 0; index < content.length; index++) {
      const character = content[index];
      const next = content[index + 1];

      if (character === '"') {
        if (quoted && next === '"') {
          cell += '"';
          index++;
        } else {
          quoted = !quoted;
        }
        continue;
      }

      if (!quoted && character === ',') {
        row.push(cell);
        cell = '';
        continue;
      }

      if (!quoted && (character === '\n' || character === '\r')) {
        if (character === '\r' && next === '\n') {
          index++;
        }

        row.push(cell);
        if (row.some((value) => value !== '')) {
          rows.push(row);
          if (rows.length >= rowLimit) {
            return rows;
          }
        }

        row = [];
        cell = '';
        continue;
      }

      cell += character;
    }

    row.push(cell);
    if (row.some((value) => value !== '') && rows.length < rowLimit) {
      rows.push(row);
    }

    return rows;
  }
}
